import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperAvatarComponent, PaperButtonComponent } from '@ricoh-paper/angular';

import { CallRecordingBadgeComponent } from '../stage/call-recording-badge.component';
import { type DockView, dockFailureKey } from '../data/call-dock.state';

/** `m:ss`, or `h:mm:ss` past an hour. Tabular numerals in the template. */
export function formatCallTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/**
 * im-call-dock — the active direct call, as one frosted pill (beads indoor_mapping_poc-0sm8.3 and .5; tickets 1.14
 * and 1.15).
 *
 * It floats bottom-centre over every page (above the bottom navigation on a phone) and carries the other person's
 * avatar and name, the call's state or timer, the recording badge when Flow is taking notes, and the controls:
 * microphone, camera, picture-in-picture (where the browser has it and they have a camera on), open the stage, and
 * hang up. A small floating control, so glass (decision 20); the accent only on the focus ring; avatars in each
 * person's colour (15c). Presentational: `im-call-dock-host` gives it the view and acts on its outputs.
 *
 * States, for the harness and the specs: `connecting`, `ringing` (they have not answered), `live` (timer),
 * `reconnecting`, and `failed` (why, and Dismiss).
 */
@Component({
  selector: 'im-call-dock',
  standalone: true,
  imports: [TranslocoPipe, PaperAvatarComponent, PaperButtonComponent, CallRecordingBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ph-no-capture' },
  template: `
    @let v = view();
    <section
      class="dock"
      role="region"
      data-testid="emp.calls.dock"
      [attr.data-phase]="v.phase"
      [attr.data-muted]="v.muted"
      [attr.aria-label]="'flow_call.dock.label' | transloco"
    >
      <div class="who">
        <span class="face">
          <paper-avatar [name]="name() || null" [image]="v.person?.picture ?? null" [size]="32" [decorative]="true" />
        </span>
        <span class="lines">
          <span class="name ph-mask" data-testid="emp.calls.dock.person">
            {{ name() || ('flow_call.dock.colleague' | transloco) }}
            @if (v.others > 0) {
              <span class="more">{{ 'flow_call.dock.and_more' | transloco: { count: v.others } }}</span>
            }
          </span>
          <span class="status" data-testid="emp.calls.dock.status" [attr.data-tone]="tone()" role="status" aria-live="polite">
            @switch (v.phase) {
              @case ('live') {
                <span class="timer" data-testid="emp.calls.dock.timer">{{ elapsed() }}</span>
                @if (v.muted) {
                  <span class="sep" aria-hidden="true">·</span>{{ 'flow_call.dock.muted' | transloco }}
                }
              }
              @case ('failed') { {{ failureKey() | transloco }} }
              @default { {{ 'flow_call.dock.phase.' + v.phase | transloco }} }
            }
          </span>
        </span>
      </div>

      @if (v.notes.state !== 'off') {
        <span class="badge" data-testid="emp.calls.dock.recording">
          <im-call-recording-badge [notes]="v.notes" />
        </span>
      }

      @if (v.audioBlocked) {
        <paper-button
          size="sm"
          shape="pill"
          intent="secondary"
          icon="volume-low"
          data-testid="emp.calls.dock.resume-audio"
          (click)="resumeAudio.emit()"
        >
          {{ 'flow_call.dock.resume_audio' | transloco }}
        </paper-button>
      }

      <div class="controls" role="toolbar" [attr.aria-label]="'flow_call.controls.label' | transloco">
        @if (v.phase === 'failed') {
          <paper-button size="sm" shape="pill" intent="secondary" data-testid="emp.calls.dock.dismiss" (click)="dismiss.emit()">
            {{ 'flow_call.dock.dismiss' | transloco }}
          </paper-button>
        } @else {
          <paper-button
            shape="icon"
            size="md"
            data-testid="emp.calls.dock.mute"
            [intent]="v.muted ? 'destructive' : 'ghost'"
            [icon]="v.muted ? 'mic-off' : 'mic'"
            [ariaPressed]="v.muted"
            [ariaLabel]="(v.muted ? 'flow_call.controls.mic_on' : 'flow_call.controls.mic_off') | transloco"
            [title]="(v.muted ? 'flow_call.controls.mic_on' : 'flow_call.controls.mic_off') | transloco"
            [disabled]="v.phase === 'connecting'"
            (click)="toggleMute.emit()"
          />
          <paper-button
            shape="icon"
            size="md"
            intent="ghost"
            data-testid="emp.calls.dock.camera"
            [icon]="v.camera ? 'video' : 'video-off'"
            [ariaPressed]="v.camera"
            [ariaLabel]="(v.camera ? 'flow_call.controls.camera_off' : 'flow_call.controls.camera_on') | transloco"
            [title]="(v.camera ? 'flow_call.controls.camera_off' : 'flow_call.controls.camera_on') | transloco"
            [disabled]="v.phase === 'connecting'"
            (click)="toggleCamera.emit()"
          />
          @if (pipSupported() && v.hasRemoteVideo) {
            <paper-button
              shape="icon"
              size="md"
              intent="ghost"
              icon="picture-in-picture"
              data-testid="emp.calls.dock.pip"
              [ariaPressed]="pipActive()"
              [ariaLabel]="(pipActive() ? 'flow_call.dock.pip_close' : 'flow_call.dock.pip_open') | transloco"
              [title]="(pipActive() ? 'flow_call.dock.pip_close' : 'flow_call.dock.pip_open') | transloco"
              (click)="togglePip.emit()"
            />
          }
          <paper-button
            shape="icon"
            size="md"
            intent="ghost"
            icon="maximize"
            data-testid="emp.calls.dock.stage"
            [ariaLabel]="'flow_call.dock.open_stage' | transloco"
            [title]="'flow_call.dock.open_stage' | transloco"
            (click)="openStage.emit()"
          />
          <paper-button
            shape="icon"
            size="md"
            intent="destructive"
            icon="phone-off"
            data-testid="emp.calls.dock.hang-up"
            [ariaLabel]="'flow_call.dock.hang_up' | transloco"
            [title]="'flow_call.dock.hang_up' | transloco"
            (click)="hangUp.emit()"
          />
        }
      </div>
    </section>
  `,
  styles: `
    @use '@ricoh-paper/angular/styles/recipes/surface' as surface;

    :host { display: block; max-width: 100%; }
    .dock {
      @include surface.glass-control;
      display: flex;
      align-items: center;
      gap: 10px;
      max-width: 100%;
      min-height: 48px;
      padding: 6px 6px 6px 8px;
      border-radius: var(--radius-pill, 100px);
      box-shadow: 0 10px 28px color-mix(in srgb, var(--text) 14%, transparent), 0 1px 2px color-mix(in srgb, var(--text) 8%, transparent);
      color: var(--text);
      font-family: var(--font-sans);
    }
    .who { display: flex; align-items: center; gap: 10px; min-width: 0; }
    .face { position: relative; display: inline-flex; flex: none; }
    .lines { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
    .name {
      max-width: 22ch;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--fs-label, 14px);
      font-weight: var(--fw-medium, 500);
    }
    .more { color: var(--text-secondary); font-weight: 400; margin-inline-start: 4px; }
    .status {
      display: flex;
      align-items: center;
      gap: 4px;
      color: var(--text-secondary);
      font-size: var(--fs-body-sm, 12px);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
    .status[data-tone='warn'] { color: var(--warning-text, var(--text-secondary)); }
    .status[data-tone='danger'] { color: var(--danger); white-space: normal; }
    .timer { color: var(--text); }
    .sep { color: var(--text-tertiary); }
    .badge { display: inline-flex; flex: none; }
    .controls { display: flex; align-items: center; gap: 2px; flex: none; margin-inline-start: auto; }

    @media (max-width: 480px) {
      .dock { gap: 8px; padding-inline-start: 6px; }
      .name { max-width: 12ch; }
      .badge { display: none; }
    }
  `,
})
export class CallDockComponent {
  readonly view = input.required<DockView>();
  readonly pipSupported = input(false);
  readonly pipActive = input(false);

  readonly toggleMute = output<void>();
  readonly toggleCamera = output<void>();
  readonly togglePip = output<void>();
  readonly openStage = output<void>();
  readonly hangUp = output<void>();
  readonly resumeAudio = output<void>();
  readonly dismiss = output<void>();

  /** A clock for the timer: one tick a second, only while the dock exists. */
  private readonly now = signal(Date.now());

  protected readonly name = computed(() => this.view().person?.name ?? '');
  protected readonly elapsed = computed(() => {
    const since = this.view().since;
    return since === null ? formatCallTime(0) : formatCallTime(this.now() - since);
  });
  protected readonly tone = computed(() => {
    const phase = this.view().phase;
    return phase === 'reconnecting' ? 'warn' : phase === 'failed' ? 'danger' : 'normal';
  });
  protected readonly failureKey = computed(() => dockFailureKey(this.view().failure));

  constructor() {
    const timer = setInterval(() => this.now.set(Date.now()), 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }
}
